import React, { useCallback } from "react"
import { MessagePrimitive, useMessage } from "@assistant-ui/react"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from "@/reducers/hooks"
import { selectAiWidgetMap } from "@/reducers/aiWidgetMapSlice"
import { AIChatTurn } from "./AIChatTurnStatus"
import { readMessageMeta } from "./aiChatMessageMeta"
import { AIChatAnswer } from "./AIChatAnswer"
import { AIChatShowMapButton } from "./AIChatShowMapButton"
import { AI_CHAT_PROSE_CLASS } from "./aiChatProse"
import { AIChatUserText } from "./AIChatUserText"
import { AIChatTachoCard } from "./tachoCard/AIChatTachoCard"
import { AIChatOrderCard } from "./orderCard/AIChatOrderCard"

export function AIChatMessage() {
  const { t } = useTranslation()
  const message = useMessage()
  const dispatch = useAppDispatch()
  const isStreaming = message.status?.type === "running"
  const { aiWidgetMap, aiWidgetTacho, aiWidgetOrder, turn, summary } =
    readMessageMeta(message)

  const handleOpenAgentMap = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      if (aiWidgetMap) {
        dispatch(selectAiWidgetMap(aiWidgetMap))
      }
    },
    [dispatch, aiWidgetMap],
  )

  const bodyText = (
    message.content as ReadonlyArray<{ type: string; text?: string }>
  )
    .filter((c) => c.type === "text" && typeof c.text === "string")
    .map((c) => c.text!)
    .join("")
  const widget = aiWidgetTacho ?? aiWidgetOrder
  const textContent = widget?.sentence ?? bodyText

  if (message.role === "assistant") {
    if (!isStreaming && !textContent && !aiWidgetMap && !widget) return null

    return (
      <MessagePrimitive.Root>
        <div className="mx-4 mb-16">
          <AIChatTurn
            turn={turn}
            summary={summary}
            answer={
              textContent && (
                <AIChatAnswer
                  className={AI_CHAT_PROSE_CLASS}
                  text={textContent}
                  isStreaming={isStreaming}
                />
              )
            }
          />
          {aiWidgetTacho && <AIChatTachoCard card={aiWidgetTacho} />}
          {aiWidgetOrder && <AIChatOrderCard card={aiWidgetOrder} />}
          {aiWidgetMap && <AIChatShowMapButton onClick={handleOpenAgentMap} />}
        </div>
      </MessagePrimitive.Root>
    )
  }

  const imageUrls = (
    message.content as ReadonlyArray<{ type: string; image?: string }>
  )
    .filter((c) => c.type === "image" && typeof c.image === "string")
    .map((c) => c.image!)

  return (
    <MessagePrimitive.Root>
      <div className="mx-4 my-10 flex justify-start">
        <div className="w-fit max-w-[85%] rounded-lg bg-blue-100 px-3 py-2 dark:bg-blue-900">
          {imageUrls.map((url) => (
            <a key={url} href={url} target="_blank" rel="noopener noreferrer">
              <img
                src={url}
                alt={t("ai.message.attachedImage")}
                className="mb-2 max-h-64 max-w-full rounded last:mb-0"
              />
            </a>
          ))}
          {textContent && (
            <p className="whitespace-pre-wrap text-base leading-relaxed text-blue-700 [overflow-wrap:anywhere] dark:text-blue-200">
              <AIChatUserText text={textContent} />
            </p>
          )}
        </div>
      </div>
    </MessagePrimitive.Root>
  )
}
